import { CrownOutlined, RocketOutlined } from '@ant-design/icons';
import { Alert, Button } from 'antd';
import React from 'react';
import { useNavigate } from 'react-router-dom';

/**
 * SubscriptionBanner - Shows subscription status and upgrade prompts
 * 
 * @param {object} subscriptionData - Subscription data from API
 * @param {boolean} showUpgradePrompt - Whether to show the upgrade CTA
 * @param {string} context - Context where banner is shown ('create' | 'limit-reached' | 'general')
 */
function SubscriptionBanner({ subscriptionData, showUpgradePrompt = true, context = 'general' }) {
    const navigate = useNavigate();

    if (!subscriptionData) {
        return null;
    }

    const isActive = subscriptionData.subscription_status === 'active';
    const eventCount = subscriptionData.event_count || 0;
    const eventLimit = subscriptionData.event_limit || 1;
    const isAtLimit = !isActive && eventCount >= eventLimit;

    // Don't show banner if user has premium
    if (isActive && context !== 'general') {
        return null;
    }

    const handleUpgradeClick = () => {
        navigate('/admin/subscription');
    };

    // Premium user banner (minimal, celebratory)
    if (isActive) {
        return (
            <Alert
                message={
                    <span>
                        <CrownOutlined style={{ marginRight: 8 }} />
                        <strong>Premium Active</strong> - You have unlimited events!
                    </span>
                }
                type="success"
                showIcon={false}
                style={{ marginBottom: 20 }}
            />
        );
    }

    // Context-specific messaging for free users
    const getContextMessage = () => {
        switch (context) {
            case 'create':
                if (isAtLimit) {
                    return {
                        type: 'warning',
                        title: '🎉 You\'ve used your free event!',
                        description: (
                            <div>
                                <p style={{ marginBottom: 12 }}>
                                    <strong>Ready to create more?</strong> Upgrade to Premium and unlock:
                                </p>
                                <ul style={{ marginBottom: 12, paddingLeft: 20 }}>
                                    <li>✨ <strong>Unlimited events</strong> - Create as many as you need</li>
                                    <li>🚀 <strong>Unlimited participants</strong> - No restrictions</li>
                                    <li>💬 <strong>Priority support</strong> - Get help when you need it</li>
                                </ul>
                                <p style={{ marginBottom: 0, fontSize: '13px', color: '#666' }}>
                                    <strong>$9/month</strong> or <strong>$88/year</strong> (save 18%) • Your first event is free forever
                                </p>
                            </div>
                        ),
                    };
                } else {
                    return {
                        type: 'info',
                        title: '🎁 Your first event is FREE!',
                        description: (
                            <div>
                                <p style={{ marginBottom: 8 }}>
                                    Try ReEvent with <strong>one free event</strong> - no credit card required!
                                </p>
                                <p style={{ marginBottom: 0, fontSize: '13px', color: '#666' }}>
                                    Love it? Upgrade to Premium for unlimited events - $9/month or $88/year (save 18%)
                                </p>
                            </div>
                        ),
                    };
                }

            case 'limit-reached':
                return {
                    type: 'error',
                    title: '🚀 Ready to grow?',
                    description: (
                        <div>
                            <p style={{ marginBottom: 12 }}>
                                You've reached your <strong>free event limit</strong> (1 event).
                                Upgrade to <strong>Premium</strong> to create unlimited events!
                            </p>
                            <p style={{ marginBottom: 12 }}>
                                <strong>Premium Benefits:</strong>
                            </p>
                            <ul style={{ marginBottom: 12, paddingLeft: 20 }}>
                                <li>✨ Unlimited events</li>
                                <li>🚀 Unlimited participants</li>
                                <li>💬 Priority support</li>
                            </ul>
                            <p style={{ marginBottom: 0 }}>
                                <strong style={{ fontSize: '16px' }}>$9/month or $88/year</strong> (save 18% annually) - Cancel anytime
                            </p>
                        </div>
                    ),
                };

            default:
                // For general context (event list page)
                if (isAtLimit) {
                    return {
                        type: 'warning',
                        title: `📊 Free Plan - ${eventCount} / ${eventLimit} event used`,
                        description: (
                            <span>
                                You've reached your free event limit. <strong>Upgrade to Premium</strong> for unlimited events - <strong>$9/month</strong> or <strong>$88/year</strong> (save 18%)
                            </span>
                        ),
                    };
                } else {
                    return {
                        type: 'info',
                        title: `📊 Free Plan - ${eventCount} / ${eventLimit} event used`,
                        description: (
                            <span>
                                You have <strong>{eventLimit - eventCount} free event{eventLimit - eventCount !== 1 ? 's' : ''}</strong> remaining.
                                Upgrade to Premium for unlimited events - $9/month or $88/year (save 18%)
                            </span>
                        ),
                    };
                }
        }
    };

    const message = getContextMessage();

    return (
        <Alert
            message={message.title}
            description={message.description}
            type={message.type}
            showIcon
            style={{ marginBottom: 20 }}
            action={
                showUpgradePrompt && (
                    <Button
                        type="primary"
                        size="small"
                        icon={<RocketOutlined />}
                        onClick={handleUpgradeClick}
                    >
                        Upgrade Now
                    </Button>
                )
            }
        />
    );
}

export default SubscriptionBanner;

